/**
 * Phase 9.5 — Minister Briefing Pack primitives (CD R10 spec).
 *
 * Shared type definitions for PHIA-calibrated language, three-scenario
 * blocks, decision options with structural dissent capture, and the
 * persona-modulated pack shape.
 *
 * Per CD R10's "both surfaces live or die on how visibly they handle
 * uncertainty, contestation, and dissent" — every primitive in this
 * folder treats those as REQUIRED, not optional.
 */

// ---------------------------------------------------------------------------
// PHIA probability yardstick (UK JIC standard, adopted verbatim per the
// minister-briefing research subagent's recommendation).
//
// "Deliberate gaps prevent false precision" — 6 bins with explicit % ranges,
// no values between bins. Plus an independent analytical-confidence rating
// (High / Moderate / Low) reflecting evidence soundness, separate from the
// likelihood itself (per IPCC AR6 convention).
// ---------------------------------------------------------------------------

export type PhiaWord =
  | 'remote'                  // <10%
  | 'improbable'              // 15-20%
  | 'realistic possibility'   // 25-50%
  | 'likely'                  // 55-70%
  | 'highly likely'           // 75-85%
  | 'almost certain';         // >90%

export interface PhiaBand {
  word: PhiaWord;
  range: string;
  /** Lower bound % (for sorting / visual scale). */
  loPct: number;
  /** Upper bound % (for sorting / visual scale). */
  hiPct: number;
}

export const PHIA_BANDS: readonly PhiaBand[] = [
  { word: 'remote',                 range: '<10%',    loPct: 0,  hiPct: 10 },
  { word: 'improbable',             range: '15–20%',  loPct: 15, hiPct: 20 },
  { word: 'realistic possibility',  range: '25–50%',  loPct: 25, hiPct: 50 },
  { word: 'likely',                 range: '55–70%',  loPct: 55, hiPct: 70 },
  { word: 'highly likely',          range: '75–85%',  loPct: 75, hiPct: 85 },
  { word: 'almost certain',         range: '>90%',    loPct: 90, hiPct: 100 },
];

export type AnalyticalConfidence = 'Low' | 'Moderate' | 'High';

/** Inline PHIA tag — every assertion in a Pack carries both a PHIA word AND
 *  an analytical-confidence rating. CD R10 Q1 verdict: PHIA tags inline +
 *  hover popover with the % range + confidence. */
export interface PhiaAssertion {
  word: PhiaWord;
  confidence: AnalyticalConfidence;
}

// ---------------------------------------------------------------------------
// Three-scenario block (SPI-M-O convention) — best / central / RWCS.
// CD R10 Q4 verdict: coarse, no false precision, visually distinct from
// the R9 counterfactual divergence chart (those are policy divergences;
// these are forecast scenarios with confidence bands).
// ---------------------------------------------------------------------------

export type ScenarioKind = 'best' | 'central' | 'reasonable-worst-case';

export interface ScenarioForecast {
  kind: ScenarioKind;
  /** Display label — "Best case" / "Central" / "Reasonable worst case". */
  label: string;
  /** Peak metric (cases, deaths, hospitalisations) as display string. */
  peak: string;
  /** When the peak hits, as display string — "wk 4" / "wk 6" / "day 50". */
  when: string;
  /** PHIA likelihood for this scenario. */
  phia: PhiaWord;
  /** Analytical confidence for the forecast itself. */
  confidence: AnalyticalConfidence;
  /** Mark as the planning case (the operator's working assumption). */
  isPlanningCase?: boolean;
  /** Optional sparkline series (oldest→newest, normalised 0-1). */
  spark?: readonly number[];
}

// ---------------------------------------------------------------------------
// Decision options with structural dissent capture.
// CD R10 Q3 verdict: dissent renders as its own row beneath each option —
// no layout exists where the pack is readable without the dissent.
// Explicit anti-SAGE-2020 design.
// ---------------------------------------------------------------------------

export interface DecisionOption {
  /** Letter id — A / B / C. */
  id: string;
  /** Action description, 1-sentence. */
  action: string;
  /** "Cases averted" estimate with CI as display string. */
  averted: string;
  /** Cost estimate with CI as display string. */
  cost: string;
  /** Political cost: Low / Moderate / High. */
  political: 'Low' | 'Moderate' | 'High';
  /** Legal authority: "Existing" / "Emergency reg" / "Needs new authority" / specific. */
  legal: string;
  /** Mark this option as the RECOMMENDED choice (operator can rotate). */
  isRecommended?: boolean;
  /** Minority view that REQUIRES surfacing. Null only when there is genuinely
   *  no dissent on record — and the dissent capture rail flags an empty
   *  dissent as "no minority view captured" so reviewers see the absence. */
  dissent: string | null;
  /** Who holds the dissent — named expert / committee / role. Surfaces
   *  alongside the minority view text in the rendered row. */
  dissentingExpert?: string;
}

// ---------------------------------------------------------------------------
// RAG strip — front-page indicator status.
// ---------------------------------------------------------------------------

export type RagStatus = 'red' | 'amber' | 'green';

export interface RagIndicator {
  /** Indicator name — "Transmission" / "Health-system load" / etc. */
  name: string;
  status: RagStatus;
  /** Short value rendered in monospace next to the dot. */
  value: string;
}

// ---------------------------------------------------------------------------
// Persona variants — 8 personas, each with its own ceiling + indicator
// strip + options framing. Per CD R10 Q5 verdict: persona reshape is a
// re-render with confirmation ("This will reshape your pack — keep
// current edits?") since each persona's pack shape differs materially.
// ---------------------------------------------------------------------------

export type Persona =
  | 'health-minister'
  | 'foreign-minister'
  | 'finance-minister'
  | 'defence-minister'
  | 'head-of-government'   // PM / President — 1pp ceiling
  | 'who-director-general'
  | 'who-regional-director'
  | 'senate-committee';

export interface PersonaSpec {
  id: Persona;
  /** Display name — "Health Minister" / "PM / President" / etc. */
  label: string;
  /** Pack page-count ceiling. Pack REFUSES to render past this. */
  pageCeiling: 1 | 2 | 3 | 4;
  /** Whether the annex page is admissible for this persona. */
  annexAllowed: boolean;
  /** BLUF angle — "clinical capacity + public-health authority" etc. */
  blufAngle: string;
  /** Default RAG indicator strip — operator can override. */
  defaultIndicators: readonly string[];
  /** Default distribution list — operator can add/remove. */
  defaultDistribution: readonly string[];
}

export const PERSONA_SPECS: Record<Persona, PersonaSpec> = {
  'health-minister': {
    id: 'health-minister',
    label: 'Health Minister',
    pageCeiling: 2,
    annexAllowed: true,
    blufAngle: 'Clinical capacity + public-health authority decisions',
    defaultIndicators: ['Transmission', 'ICU occupancy %', 'Vent stock-days', 'Vaccine on-hand', 'Test positivity', 'HCW absence'],
    defaultDistribution: ['Minister', 'Permanent Secretary', 'CMO', 'PMO health lead'],
  },
  'foreign-minister': {
    id: 'foreign-minister',
    label: 'Foreign Minister',
    pageCeiling: 2,
    annexAllowed: true,
    blufAngle: 'International relations + travel-restriction implication',
    defaultIndicators: ['Cross-border risk', 'Diplomatic exposure', 'Travel-advisory tier', 'Bilateral asks', 'PHEIC status', 'Embassy stance'],
    defaultDistribution: ['Minister', 'Permanent Secretary', 'Ambassador (affected jurisdictions)'],
  },
  'finance-minister': {
    id: 'finance-minister',
    label: 'Finance Minister',
    pageCeiling: 2,
    annexAllowed: true,
    blufAngle: 'Fiscal impact + emergency-spending authority',
    defaultIndicators: ['Health-system cost', 'Economy impact', 'Emergency reserve drawdown', 'Insurance trigger', 'Aid availability', 'GDP forecast delta'],
    defaultDistribution: ['Minister', 'Treasury Secretary', 'Central Bank', 'PMO economic lead'],
  },
  'defence-minister': {
    id: 'defence-minister',
    label: 'Defence Minister',
    pageCeiling: 2,
    annexAllowed: true,
    blufAngle: 'Military mobilisation, base health, force-protection',
    defaultIndicators: ['Force readiness', 'Base health', 'Logistics throughput', 'Civil-mil tasking', 'Deployment exposure', 'Bio-defence posture'],
    defaultDistribution: ['Minister', 'CDF', 'Surgeon-General', 'PMO defence lead'],
  },
  'head-of-government': {
    id: 'head-of-government',
    label: 'PM / President',
    pageCeiling: 1,
    annexAllowed: false,
    blufAngle: 'Distilled cross-portfolio judgment — escalation tier',
    defaultIndicators: ['Health', 'Economy', 'Public-Order', 'International-Reputation'],
    defaultDistribution: ['PM', 'Cabinet Secretary', 'NSA', 'CoS', 'Press Secretary'],
  },
  'who-director-general': {
    id: 'who-director-general',
    label: 'WHO Director-General',
    pageCeiling: 3,
    annexAllowed: true,
    blufAngle: 'Cross-regional + IHR-compliance angle; PHEIC consideration',
    defaultIndicators: ['Countries in response', 'PHEIC proximity', 'GOARN deployments', 'Vaccine equity', 'Member-state IHR gaps', 'EC convening status'],
    defaultDistribution: ['DG', 'EXD-WHE', 'Regional Directors', 'IHR Focal Points'],
  },
  'who-regional-director': {
    id: 'who-regional-director',
    label: 'WHO Regional Director',
    pageCeiling: 4,
    annexAllowed: true,
    blufAngle: 'Cross-country aggregation + IHR Article 6/7 notification status',
    defaultIndicators: ['Active responses (region)', 'PHEIC proximity', 'GOARN deployments', 'Vaccine equity (region)', 'Member-state IHR core capacities', 'Sub-regional bloc coordination'],
    defaultDistribution: ['RD', 'DG office', 'EXD-WHE', 'Country office reps'],
  },
  'senate-committee': {
    id: 'senate-committee',
    label: 'Senate / Cabinet Committee',
    pageCeiling: 4,
    annexAllowed: true,
    blufAngle: 'Oversight + appropriations + cross-portfolio coordination',
    defaultIndicators: ['Health', 'Economy', 'Authority used', 'Spending to-date', 'International posture', 'Lessons-applied'],
    defaultDistribution: ['Committee chair', 'Committee members', 'Committee clerk', 'Briefing officials'],
  },
};

// ---------------------------------------------------------------------------
// Provenance — CD R10 Q10 "3-state origin tag" for auto-attached content
// (Cultural Risk Layer factors that the Pack pulls in automatically).
// ---------------------------------------------------------------------------

export type ProvenanceOrigin =
  /** Auto-attached by the Pack builder from a CulturalRiskFactor match. */
  | 'auto-attached'
  /** Operator added manually. */
  | 'operator-added'
  /** Operator-suppressed an auto-attached entry (renders crossed-out). */
  | 'operator-suppressed';

// ---------------------------------------------------------------------------
// Classification banner — UK GSCP-style.
// ---------------------------------------------------------------------------

export type Classification =
  | 'UNCLASSIFIED'
  | 'OFFICIAL'
  | 'OFFICIAL-SENSITIVE'
  | 'SECRET'
  | 'TOP-SECRET';

// ---------------------------------------------------------------------------
// Pack metadata — the header band on every page.
// ---------------------------------------------------------------------------

export interface PackHeader {
  classification: Classification;
  /** ISO datetime — "2026-05-31T06:00:00Z" rendered as "2026-05-31 0600Z". */
  asOf: string;
  /** Distribution list, comma-separated. */
  distribution: string;
  /** Pack id — "BM-2271" / etc. */
  packId: string;
  /** Estimated read time in minutes. */
  readTimeMin: number;
  /** Page number within the pack (P1 of N). */
  page: number;
  totalPages: number;
}

// ---------------------------------------------------------------------------
// Cadence — CD R10 Q7 "cadence-as-surface-state."
// ---------------------------------------------------------------------------

export type Cadence = 'morning' | 'end-of-day' | 'urgent-flash';

export const CADENCE_META: Record<Cadence, { label: string; description: string; skipDefencePack: boolean }> = {
  morning:       { label: 'Morning brief',   description: 'Daily steady-state; full pack', skipDefencePack: false },
  'end-of-day':  { label: 'End of day',      description: 'Daily steady-state; full pack',  skipDefencePack: false },
  'urgent-flash': { label: 'Urgent FLASH',    description: 'P1 only — skip defence pack',   skipDefencePack: true },
};
